import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Users2, Upload, Loader2, CheckCircle2, X } from "lucide-react";
import api from "@/lib/api";
import { resolveTrainerImage } from "@/lib/trainers";
import { useCms } from "@/lib/cms";

const EMPTY = {
  photo: "", full_name: "", gender: "", date_of_birth: "", designation: "", school: "", previous_schools: "",
  subject_expertise: "", skills: "", qualifications: "", certifications: "", experience: "", achievements: "",
  bio: "", teaching_philosophy: "", languages: "", preferred_location: "", address: "", city: "", state: "", country: "India",
  email: "", phone: "", linkedin: "", website: "", portfolio_url: "", youtube: "", resume_url: "", certificate_urls: [],
  consent: false, privacy_policy_accepted: false, terms_accepted: false,
};

const inputCls = "w-full px-3.5 py-2.5 rounded-[14px] border outline-none text-sm bg-white transition-all";
const inputStyle = { borderColor: "#DCE4EC" };
const fh = {
  onFocus: (e) => { e.target.style.borderColor = "#3B68E7"; e.target.style.boxShadow = "0 0 0 4px #3B68E71A"; },
  onBlur: (e) => { e.target.style.borderColor = "#DCE4EC"; e.target.style.boxShadow = "none"; },
};
const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-xs font-semibold mb-1.5 block" style={{ color: "#64748B" }}>{label}{required && <span style={{ color: "#DC2626" }}> *</span>}</span>
    {children}
  </label>
);

export default function ExpertApply() {
  const { site } = useCms();
  const brandName = site?.company_name || "";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isResubmit = searchParams.get("resubmit") === "1";
  const [data, setData] = useState(() => {
    if (searchParams.get("resubmit") === "1") {
      return { ...EMPTY, email: searchParams.get("email") || "", phone: searchParams.get("phone") || "" };
    }
    try {
      const draft = localStorage.getItem("expert_apply_draft");
      return draft ? { ...EMPTY, ...JSON.parse(draft) } : EMPTY;
    } catch {
      return EMPTY;
    }
  });

  // Autosave draft locally only — never sent anywhere until the applicant actually
  // submits. Skipped in resubmit mode so a stale unrelated draft can't overwrite the
  // prefilled email/phone for the application being resubmitted.
  useEffect(() => {
    if (isResubmit) return;
    localStorage.setItem("expert_apply_draft", JSON.stringify(data));
  }, [data, isResubmit]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState("");
  const [uploadingResume, setUploadingResume] = useState(false);
  const [uploadingCerts, setUploadingCerts] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/experts/upload", fd);
    return r.data.url;
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(URL.createObjectURL(file));
    setUploadingPhoto(true);
    try { set("photo", await uploadFile(file)); } catch (err) { toast.error(err?.response?.data?.detail || "Photo upload failed"); }
    finally { setUploadingPhoto(false); }
  };
  const onResume = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingResume(true);
    try { set("resume_url", await uploadFile(file)); toast.success("Resume uploaded"); } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploadingResume(false); }
  };
  const onCertificates = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingCerts(true);
    try {
      const urls = await Promise.all(files.map(uploadFile));
      set("certificate_urls", [...data.certificate_urls, ...urls]);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploadingCerts(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!(data.consent && data.privacy_policy_accepted && data.terms_accepted)) {
      setError("Please accept the declaration, privacy policy, and terms & conditions.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        skills: data.skills.split(",").map((s) => s.trim()).filter(Boolean),
        qualifications: data.qualifications.split("\n").map((s) => s.trim()).filter(Boolean),
        certifications: data.certifications.split("\n").map((s) => s.trim()).filter(Boolean),
        achievements: data.achievements.split("\n").map((s) => s.trim()).filter(Boolean),
        previous_schools: data.previous_schools.split("\n").map((s) => s.trim()).filter(Boolean),
        languages: data.languages.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (isResubmit) {
        await api.put("/experts/applications/resubmit", payload, { params: { email: data.email, phone: data.phone } });
      } else {
        await api.post("/experts/apply", payload);
      }
      setSuccess(true);
      localStorage.removeItem("expert_apply_draft");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not submit your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div style={{ backgroundColor: "#F8FAFC" }} className="min-h-[70vh] flex items-center justify-center px-5">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full grid place-items-center mx-auto mb-5" style={{ backgroundColor: "#ECFDF5" }}>
            <CheckCircle2 className="w-8 h-8" style={{ color: "#16A34A" }} />
          </div>
          <h1 className="rebrand-serif text-2xl font-bold" style={{ color: "#1A202C" }}>Application submitted!</h1>
          <p className="mt-3" style={{ color: "#64748B" }}>Our team will review your application and get back to you soon.</p>
          <button onClick={() => navigate("/experts")} className="mt-6 text-sm font-semibold text-white transition-transform hover:scale-[1.02]" style={{ backgroundColor: "#3B68E7", borderRadius: 50, padding: "12px 24px" }}>
            View Expert Network
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "#F8FAFC" }} data-testid="expert-apply-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-16 sm:py-20">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <div className="w-12 h-12 rounded-full grid place-items-center mx-auto mb-4" style={{ backgroundColor: "#3B68E714" }}>
            <Users2 className="w-5 h-5" style={{ color: "#3B68E7" }} />
          </div>
          <h1 className="rebrand-serif font-bold tracking-tight" style={{ fontSize: "clamp(2rem, 4vw + 1rem, 3rem)", color: "#1A202C" }}>
            {isResubmit ? "Update Your Application" : "Join India's Growing Community of Skilled Educators"}
          </h1>
          <p className="mt-3 max-w-xl mx-auto" style={{ color: "#64748B" }}>
            {isResubmit
              ? "Make the requested changes below and resubmit — this updates your existing application."
              : `Experienced educators, trainers, and academic experts can become part of ${brandName}'s Expert Network.`}
          </p>
        </motion.div>

        <form onSubmit={submit} className="bg-white p-6 sm:p-10 grid gap-4" style={{ borderRadius: 24, border: "1px solid #E2E8F0", boxShadow: "0 10px 25px rgba(0,0,0,0.05)" }}>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-100 shrink-0 grid place-items-center">
              {(photoPreview || data.photo) ? <img src={photoPreview || resolveTrainerImage(data.photo)} alt="" className="w-full h-full object-cover" /> : <Users2 className="w-6 h-6 text-gray-300" />}
            </div>
            <label className="inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer" style={{ border: "1px solid #E2E8F0", borderRadius: 50, padding: "8px 14px", color: "#1A202C" }}>
              {uploadingPhoto ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Upload profile photo
              <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Full name" required><input required value={data.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
            <Field label="Gender">
              <select value={data.gender} onChange={(e) => set("gender", e.target.value)} className={inputCls} style={inputStyle} {...fh}>
                <option value="">Select…</option>
                <option>Female</option><option>Male</option><option>Prefer not to say</option><option>Other</option>
              </select>
            </Field>
            <Field label="Date of birth"><input type="date" value={data.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
            <Field label="Email" required><input required type="email" value={data.email} onChange={(e) => set("email", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
            <Field label="Mobile number" required><input required value={data.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
          </div>
          <Field label="Address"><input value={data.address} onChange={(e) => set("address", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="City"><input value={data.city} onChange={(e) => set("city", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
            <Field label="State"><input value={data.state} onChange={(e) => set("state", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
            <Field label="Country"><input value={data.country} onChange={(e) => set("country", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
          </div>

          <div className="pt-3 mt-1" style={{ borderTop: "1px solid #F1F5F9" }}>
            <div className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: "#3B68E7" }}>Professional Information</div>
            <div className="grid gap-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Current school / organization" required><input required value={data.school} onChange={(e) => set("school", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
                <Field label="Designation" required><input required value={data.designation} onChange={(e) => set("designation", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
              </div>
              <Field label="Previous schools (one per line)"><textarea value={data.previous_schools} onChange={(e) => set("previous_schools", e.target.value)} rows={2} className={`${inputCls} resize-none`} style={inputStyle} {...fh} /></Field>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Subject expertise" required><input required value={data.subject_expertise} onChange={(e) => set("subject_expertise", e.target.value)} className={inputCls} style={inputStyle} {...fh} placeholder="e.g. STEM, Assessment" /></Field>
                <Field label="Skills (comma separated)"><input value={data.skills} onChange={(e) => set("skills", e.target.value)} className={inputCls} style={inputStyle} {...fh} placeholder="Curriculum design, AI in classrooms" /></Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Qualifications (one per line)"><textarea value={data.qualifications} onChange={(e) => set("qualifications", e.target.value)} rows={2} className={`${inputCls} resize-none`} style={inputStyle} {...fh} /></Field>
                <Field label="Certifications (one per line)"><textarea value={data.certifications} onChange={(e) => set("certifications", e.target.value)} rows={2} className={`${inputCls} resize-none`} style={inputStyle} {...fh} /></Field>
              </div>
              <Field label="Achievements (one per line)"><textarea value={data.achievements} onChange={(e) => set("achievements", e.target.value)} rows={2} className={`${inputCls} resize-none`} style={inputStyle} {...fh} /></Field>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Experience"><input value={data.experience} onChange={(e) => set("experience", e.target.value)} className={inputCls} style={inputStyle} {...fh} placeholder="e.g. 12 years" /></Field>
                <Field label="Languages known (comma separated)"><input value={data.languages} onChange={(e) => set("languages", e.target.value)} className={inputCls} style={inputStyle} {...fh} placeholder="English, Hindi" /></Field>
                <Field label="Preferred training location"><input value={data.preferred_location} onChange={(e) => set("preferred_location", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
              </div>
            </div>
          </div>

          <div className="pt-3 mt-1" style={{ borderTop: "1px solid #F1F5F9" }}>
            <div className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: "#3B68E7" }}>Biography</div>
            <Field label="About yourself"><textarea value={data.bio} onChange={(e) => set("bio", e.target.value)} rows={3} className={`${inputCls} resize-none`} style={inputStyle} {...fh} /></Field>
            <Field label="Teaching philosophy"><textarea value={data.teaching_philosophy} onChange={(e) => set("teaching_philosophy", e.target.value)} rows={3} className={`${inputCls} resize-none mt-3`} style={inputStyle} {...fh} /></Field>
          </div>

          <div className="pt-3 mt-1" style={{ borderTop: "1px solid #F1F5F9" }}>
            <div className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: "#3B68E7" }}>Online Presence</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="LinkedIn"><input value={data.linkedin} onChange={(e) => set("linkedin", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
              <Field label="Website"><input value={data.website} onChange={(e) => set("website", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
              <Field label="Portfolio (optional link)"><input value={data.portfolio_url} onChange={(e) => set("portfolio_url", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
              <Field label="YouTube (optional)"><input value={data.youtube} onChange={(e) => set("youtube", e.target.value)} className={inputCls} style={inputStyle} {...fh} /></Field>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer" style={{ border: "1px solid #E2E8F0", borderRadius: 50, padding: "10px 16px", color: "#1A202C" }}>
              {uploadingResume ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {data.resume_url ? "Resume uploaded ✓" : "Upload resume"}
              <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={onResume} />
            </label>
            <label className="inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer" style={{ border: "1px solid #E2E8F0", borderRadius: 50, padding: "10px 16px", color: "#1A202C" }}>
              {uploadingCerts ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Upload certificates ({data.certificate_urls.length})
              <input type="file" accept=".pdf,image/*" multiple className="hidden" onChange={onCertificates} />
            </label>
          </div>
          {data.certificate_urls.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {data.certificate_urls.map((u, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-gray-100">
                  Certificate {i + 1}
                  <button type="button" onClick={() => set("certificate_urls", data.certificate_urls.filter((_, idx) => idx !== i))}><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          )}

          <label className="flex items-start gap-2 text-sm font-medium pt-2" style={{ color: "#1A202C", borderTop: "1px solid #F1F5F9" }}>
            <input type="checkbox" required checked={data.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5" data-testid="expert-consent" />
            I certify that the information provided is accurate.
          </label>
          <label className="flex items-start gap-2 text-sm" style={{ color: "#374151" }}>
            <input type="checkbox" required checked={data.privacy_policy_accepted} onChange={(e) => set("privacy_policy_accepted", e.target.checked)} className="mt-0.5" data-testid="expert-privacy" />
            I have read and accept the Privacy Policy.
          </label>
          <label className="flex items-start gap-2 text-sm" style={{ color: "#374151" }}>
            <input type="checkbox" required checked={data.terms_accepted} onChange={(e) => set("terms_accepted", e.target.checked)} className="mt-0.5" data-testid="expert-terms" />
            I have read and accept the Terms &amp; Conditions.
          </label>

          {error && <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs px-3 py-2.5">{error}</div>}

          <div className="flex justify-end pt-1">
            <button type="submit" disabled={submitting} data-testid="expert-apply-submit" className="text-white text-sm font-semibold transition-transform hover:scale-[1.02] disabled:opacity-60" style={{ backgroundColor: "#3B68E7", borderRadius: 50, padding: "12px 28px", boxShadow: "0 4px 12px rgba(59,104,231,0.3)" }}>
              {submitting ? "Submitting…" : isResubmit ? "Resubmit Application" : "Submit Application"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
